<template>
    <v-form data-vv-scope="flw-account">
        <v-row v-if="filteredTypes && filteredTypes.length > 0" class="align-content-center">
            <v-col class="pa-0 px-3 pt-3 pa-lg-3">
                <v-select
                  v-model="type"
                  :items="filteredTypes"
                  :item-text="(item) => {
                    return item.method.replace('-', ' ')
                      .replace(/^\w/, (s) => s.toUpperCase());
                  }"
                  return-object
                  label="Type"
                  name="type"
                  color="primary"
                  :disabled="!editable"
                  :readonly="!editable"
                  outlined
                ></v-select>
            </v-col>
        </v-row>

        <v-row class="align-content-center">
            <v-col class="pa-0 px-3 pt-3 pa-lg-3">
                <v-text-field
                    v-model="account_number"
                    type="text"
                    :label="accountNumberLabel"
                    name="account_number"
                    color="primary"
                    :disabled="!editable"
                    v-validate="isMobileMoney ? 'required|international-phone' : 'required'"
                    :error-messages="errors.first('flw-account.account_number')"
                    :readonly="!editable"
                    outlined
                    ref="account_number"
                />
            </v-col>
        </v-row>

        <v-row class="align-content-center">
            <v-col class="pa-0 px-3 pt-3 pa-lg-3">
                <v-text-field
                    v-model="confirm_account_number"
                    type="text"
                    :label="confirmAccountNumberLabel"
                    name="confirm_account_number"
                    color="primary"
                    :disabled="!editable"
                    v-validate="'confirmed:account_number'"
                    :error-messages="errors.first('flw-account.confirm_account_number')"
                    :readonly="!editable"
                    @paste.prevent="() => {}"
                    outlined
                />
            </v-col>
        </v-row>

        <v-row class="align-content-center" v-if="showBVN">
            <v-alert
                dense
                dismissible
                class="mx-3"
                color="primary"
                type="info"
            >
                BVN is used by <b>Kaoshi</b> to verify your identity,
                allowing us prevent identity-theft related frauds.
                We do not store your BVN
            </v-alert>

            <v-col class="py-0">
                <v-text-field
                    v-model="bvn"
                    type="text"
                    label="BVN"
                    name="bvn"
                    color="primary"
                    :disabled="!editable"
                    :readonly="!editable"
                    outlined
                />
            </v-col>
        </v-row>

        <v-row class="align-content-center">
            <v-col class="pa-0 px-3 pt-3 pa-lg-3">
                <v-autocomplete
                    v-model="bank"
                    :items="filteredBanks"
                    :item-text="'Name'"
                    return-object
                    :label="isMobileMoney ? 'Mobile Money Provider' : 'Bank'"
                    name="bank"
                    color="primary"
                    v-validate="'required'"
                    :disabled="!editable"
                    :readonly="!editable"
                    :menu-props="{ offsetY: true }"
                    outlined
                />
            </v-col>
        </v-row>

        <v-row
            v-if="allowBranches && branches && !isMobileMoney"
            class="align-content-center"
        >
          <v-col class="pa-0 px-3 pt-3 pa-lg-3">
            <v-select
              v-if="branches.length"
              v-model="branch"
              :items="branches"
              :item-text="'BranchName'"
              return-object
              label="Branch"
              name="branch"
              color="primary"
              v-validate="'required'"
              :disabled="!editable"
              :readonly="!editable"
              outlined
            ></v-select>

            <v-text-field
                v-else
                v-model="branch_code"
                type="text"
                label="Branch Name"
                name="branch_code"
                color="primary"
                v-validate="'required'"
                :disabled="!editable"
                :readonly="!editable"
                outlined
            />
          </v-col>
        </v-row>


        <v-row v-if="editable || removable" class="align-content-center">
            <v-col class="pt-0">
                <v-btn color="primary" type="button" text @click="saveAccount" v-if="editable">
                    Save
                </v-btn>
                <v-btn color="primary" type="button" text @click="removeAccount" v-if="removable">
                    Remove Account
                </v-btn>
            </v-col>
        </v-row>
    </v-form>
</template>

<script>
import { sortBy } from 'lodash';
import FlutterwaveService from '../../../services/FlutterwaveService';

export default {
    props: getProps(),
    data() {
        return {
            bvn: null,
            account_number: null,
            confirm_account_number: null,
            bank: null,
            bank_code: null,
            branches: null,
            branch: null,
            branch_code: null,
            type: null,
            banks: [],
        };
    },
    watch: getWatch(),
    mounted,
    methods: getMethods(),
    computed: {
        filteredBanks() {
            if (!this.type) return this.banks;
            const banks = this.banks.filter((bank) => {
                return !!bank.IsMobileVerified === (this.type.method === 'mobile-money');
            });

            return sortBy(banks, (bank) => bank && bank.Name.toLowerCase());
        },
        countryIsNigeria() {
          return this.$store.state.user.country_abbreviation === 'NG';
        },
        filteredTypes() {
          if(this.countryIsNigeria) return this.types.filter(key => key.method !== 'mobile-money');
          return this.types;
        },
        accountNumberLabel() {
          return !this.isMobileMoney ? 'Account Number' : 'Mobile Money Number';
        },
        confirmAccountNumberLabel() {
          return !this.isMobileMoney ? 'Confirm Account Number' : 'Confirm Mobile Money Number';
        },
        isMobileMoney() {
            return this.type && this.type.method === 'mobile-money';
        },
    },
};

function getProps() {
    return {
        input: {
            type: Object,
            default: () => ({}),
        },
        editable: {
            type: Boolean,
            default: true,
        },
        removable: {
            type: Boolean,
            default: true,
        },
        abbreviation: String,
        showBVN: {
            type: Boolean,
            default: false,
        },
        allowBranches: {
            type: Boolean,
            default: false,
        },
        types: Array,
        allowedBankCodes: Array,
    };
}

function getWatch() {
    return {
        input: function(data) {
            this.setData(data);
        },
        bank: function(bank) {
            if (this.allowBranches && bank && bank.Id) {
                this.loadBranches(bank.Id);
            }
        },
    };
}

async function mounted() {
    await this.loadBanks(this.abbreviation);
    this.setData(this.input);
    if (!this.type) {
        this.type = this.types[0];
    }
}

function getMethods() {
    return {
        setData(data) {
            this.bank_code = data.bank_code;
            this.account_number = data.account_number;
            this.bvn = data.bvn;
            this.branch_code = data.branch_code;
            this.bank = this.banks.find((item) => item.Code === this.bank_code);
            this.type = this.types.find((item) => item.id === data.typeId);
        },
        async saveAccount() {
            const valid = await this.$validator.validateAll('flw-account');
            if (!valid) {
                return;
            }

            const account = {
                account_number: this.account_number,
                bank_code: this.bank.Code,
            };

            if (this.showBVN) {
              account.bvn = this.bvn;
            }

            if (this.allowBranches) {
                if (this.branch) {
                    account.branch_code = this.branch.BranchCode;
                }

                if (!this.branch && this.branch_code) {
                    account.branch_code = this.branch_code;
                }
            }

            const payload = {
                account,
                typeId: this.type ? this.type.id : null,
            };

            this.$emit('save', payload);
        },
        removeAccount() {
            this.$emit('remove');
        },
        loadBanks(abbreviation) {
            this.$emit('loading');
            return FlutterwaveService.getBanksByCountry(abbreviation)
                .then((banks) => {
                    if (banks.length === 0) {
                        this.$swal({
                            type: 'error',
                            title: 'No banks found',
                        });
                    } else {
                        if (abbreviation === 'NG') {
                            this.banks = banks;
                        } else {
                            this.banks = banks.filter((b) => this.allowedBankCodes.indexOf(b.Code) >= 0);
                        }
                    }
                    this.$emit('loaded');
                })
                .catch((err) => {
                    this.$emit('loaded');
                    this.$swal({
                        type: 'error',
                        title: 'Error banks',
                    });
                });
        },
        loadBranches(bankId) {
          this.$emit('loading');
          FlutterwaveService.getBranchesByBankId(bankId)
              .then((branches) => {
                  this.$emit('loaded');
                  this.branches = branches;
                  if (this.branch_code) {
                      this.branch = this.branches.find((item) => item.BranchCode === this.branch_code);
                  }
              })
              .catch((err) => {
                  this.$emit('loaded');
                  this.$swal({
                      type: 'error',
                      title: 'Error loading branches',
                  });
              })
        },
    };
}
</script>
